/**
 * A map that re-measures itself whenever its box changes - FRONTEND.md §9.692, FB-1002.
 *
 * **Leaflet measures its container when it is created and then trusts that figure.** A map created in
 * a box that is not its final size draws its tiles for the wrong one: grey, or a strip of tiles in a
 * corner, until the window happens to be resized. The zone editor mounts its map in a pane that is
 * `hidden` on a phone until the *Mapa* tab is chosen - a 0 x 0 box - and nothing called
 * `invalidateSize`. §9.694's map lives in a disclosure, which is the same state by a second route.
 *
 * **One rule for every way a box changes**: a `ResizeObserver` on the container, and `invalidateSize()`
 * whenever the box takes a new size that has room in it. A box that shrinks to nothing is left alone -
 * there is nothing to lay out into, and Leaflet would re-centre on a point - and the next real size is
 * measured afresh.
 *
 * **Structural rather than Leaflet's own `Map` type**, so this file imports nothing from Leaflet and
 * `@agos/ui`'s main entry stays free of it; `PinMap` and the zone editor each hand over their map.
 *
 * Returns the disconnect, so a `useEffect` can return it directly.
 */
export interface SizedMap {
  getContainer(): HTMLElement
  invalidateSize(): unknown
}

/** The part of `ResizeObserver` this uses - narrow, so a test can hand in a fake. */
export interface BoxObserver {
  observe(target: Element): void
  disconnect(): void
}

export type BoxObserverConstructor = new (callback: () => void) => BoxObserver

/** The browser's own, when there is one. jsdom has none, and a map there has nothing to measure. */
function browserObserver(): BoxObserverConstructor | undefined {
  return typeof globalThis.ResizeObserver === 'function' ? globalThis.ResizeObserver : undefined
}

export function keepMapSized(
  map: SizedMap,
  Observer: BoxObserverConstructor | undefined = browserObserver(),
): () => void {
  if (Observer === undefined) return () => undefined

  const container = map.getContainer()
  let last: { width: number; height: number } | null = null

  const observer = new Observer(() => {
    const width = container.clientWidth
    const height = container.clientHeight
    // Hidden, or not laid out yet: there is nothing to draw into. Remembered as such, so the next real
    // size counts as a change even when it equals the size before the box was hidden.
    if (width === 0 || height === 0) {
      last = { width, height }
      return
    }
    if (last !== null && last.width === width && last.height === height) return
    last = { width, height }
    map.invalidateSize()
  })
  observer.observe(container)
  return () => observer.disconnect()
}
